import { type FC, useCallback, useReducer, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Column,
  DownloadIcon,
  EmptyState,
  Heading,
  HiddenFormLabel,
  IconBox,
  Row,
  SearchInput,
  Spinner,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { useQueryClient } from "react-query";

import { isExpiredCursor } from "src/components/lineage/lineage-pagination";
import { useLineageExport } from "src/components/lineage/use-lineage-export";
import { useRebuildPollLimit } from "src/components/lineage/use-rebuild-poll-limit";
import { getDisplayNameForResource } from "src/components/lineage/usage-panel/resource-display";
import { SkeletonItem } from "src/components/skeletons";
import {
  LineageStatus,
  type ResourceDependentsPageQuery,
  useResourceDependentsPageQuery,
  useResourceDependentsSummaryQuery,
} from "src/graphql";
import {
  type GovernedRefetchQuery,
  useGovernedRefetchInterval,
} from "src/hooks/use-governed-refetch-interval";
import {
  formatDatetimeWithTimezone,
  formatRelativeShort,
} from "src/utils/time";

import {
  loadResourceDependentsPage,
  RESOURCE_DEPENDENTS_PAGE_SIZE,
} from "./resource-dependents-page-loader";
import { ResourceTypeFilter } from "./resource-type-filter";
import { UsageSummary } from "./usage-summary";
import { UsageTree } from "./usage-tree";
import {
  dispatchIfDifferentRead,
  emptyTreeState,
  usageTreeReducer,
  type UsageRow,
} from "./usage-tree-state";

const PAGE_LOAD_ERROR =
  "Something went wrong on our end. Try again, or contact support if it keeps happening.";

const QUERY_OPTIONS = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

const USAGE_CACHE_TIME_MS = 5 * 60 * 1000; // 5 minutes

// The tree variant drops the summary, filters, and search
type UsagePanelVariant = "full" | "tree";

type UsagePanelProps = {
  resourceType: string;
  resourceId: string;
  variant?: UsagePanelVariant;
  name?: string;
  lastUpdatedAt?: string;
  // Fill the height of the container.
  fillHeight?: boolean;
  alwaysRefetch?: boolean;
  // Offer a CSV of every downstream resource, not just the expanded rows.
  canExportCsv?: boolean;
};

// Stands in for the tree rows, which are a row of text at py={1.5}.
const UsageRowsSkeleton: FC = () => (
  <Column gap={1}>
    {[0, 1, 2, 3, 4].map((index) => (
      <SkeletonItem key={index} borderRadius="md" height={8} />
    ))}
  </Column>
);

type UsagePanelBodyProps = Pick<
  UsagePanelProps,
  | "resourceType"
  | "resourceId"
  | "variant"
  | "fillHeight"
  | "alwaysRefetch"
  | "canExportCsv"
>;

type UsagePanelHeaderProps = {
  resourceType: string;
  name: string;
  lastUpdatedAt?: string;
};

/**
 * Used to display all the downstream resources for a given resource.
 * This panel fetches data using the lineage graph API.
 */
export const UsagePanel: FC<UsagePanelProps> = ({
  resourceType,
  resourceId,
  variant = "full",
  name,
  lastUpdatedAt,
  fillHeight,
  alwaysRefetch,
  canExportCsv,
}) => {
  return (
    <Column flex={fillHeight ? 1 : undefined} minHeight={0}>
      {name && (
        <UsagePanelHeader
          resourceType={resourceType}
          name={name}
          lastUpdatedAt={lastUpdatedAt}
        />
      )}
      <UsagePanelBody
        key={`${resourceType}:${resourceId}`}
        resourceType={resourceType}
        resourceId={resourceId}
        variant={variant}
        fillHeight={fillHeight}
        alwaysRefetch={alwaysRefetch}
        canExportCsv={canExportCsv}
      />
    </Column>
  );
};

const UsagePanelBody: FC<UsagePanelBodyProps> = ({
  resourceType,
  resourceId,
  variant,
  fillHeight,
  alwaysRefetch,
  canExportCsv,
}) => {
  const isTreeOnly = variant === "tree";
  const freshness = alwaysRefetch
    ? { cacheTime: 0, refetchOnMount: "always" as const }
    : { cacheTime: USAGE_CACHE_TIME_MS, refetchOnMount: false as const };
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(
    usageTreeReducer,
    undefined,
    emptyTreeState,
  );
  const [search, setSearch] = useState("");
  const [isReloading, setIsReloading] = useState(false);
  const { isExporting, exportLineage } = useLineageExport();

  // Refetch the page when the status changes or the partitions change.
  const { refetchInterval: getGovernedRefetchInterval } =
    useGovernedRefetchInterval<ResourceDependentsPageQuery>({
      baseMs: 5000,
      compareKey: (data) => [
        data?.resourceDependentsPage.status,
        ...(data?.resourceDependentsPage.partitions ?? []).map(
          (partition) =>
            `${partition.ownerResourceType}:${partition.status}:${partition.reason}`,
        ),
      ],
    });

  // While the graph rebuilds, poll it. The interval stretches on its own as the
  // graph stops changing, and the poll stops for good once the rebuild has taken
  // more attempts than a rebuild should need.
  const rebuildPoll = useRebuildPollLimit();
  const getRebuildingRefetchInterval = useCallback(
    (
      data: ResourceDependentsPageQuery | undefined,
      query?: GovernedRefetchQuery,
    ) =>
      data?.resourceDependentsPage.status === LineageStatus.Rebuilding &&
      !rebuildPoll.hasGivenUp
        ? getGovernedRefetchInterval(data, query)
        : false,
    [getGovernedRefetchInterval, rebuildPoll.hasGivenUp],
  );

  const firstPageQuery = useResourceDependentsPageQuery(
    {
      resourceType,
      resourceId,
      limit: RESOURCE_DEPENDENTS_PAGE_SIZE,
    },
    {
      ...QUERY_OPTIONS,
      ...freshness,
      keepPreviousData: false,
      refetchInterval: getRebuildingRefetchInterval,
      onSuccess: (data) =>
        rebuildPoll.observe(data.resourceDependentsPage.status),
      // react-query keeps the last successful data on error, so without this a
      // failing read would leave a rebuilding status on screen and the poll
      // armed against it forever.
      onError: () => rebuildPoll.observe(undefined),
    },
  );

  // Each new result goes into the reducer during render, so nothing renders from a stale state.
  // An effect would paint the stale state first, and onSuccess never fires for a cached result.
  dispatchIfDifferentRead(
    dispatch,
    "first-page-read",
    {
      data: firstPageQuery.data?.resourceDependentsPage,
      isLoading: firstPageQuery.isLoading,
      error: firstPageQuery.error,
    },
    state.firstPage,
  );

  // Every later read should use the same snapshot token.
  const { snapshotToken, hasExpired } = state;

  // Summary is used to display the total number of downstream resources
  // at the top of the panel.
  const summaryQuery = useResourceDependentsSummaryQuery(
    {
      resourceType,
      resourceId,
      snapshotToken: snapshotToken ?? "",
    },
    {
      ...QUERY_OPTIONS,
      ...freshness,
      enabled: !isTreeOnly && Boolean(snapshotToken) && !hasExpired,
      keepPreviousData: false,
      onError: (error) => {
        if (snapshotToken && isExpiredCursor(error)) {
          dispatch({ type: "snapshot-expired", snapshotToken });
        }
      },
    },
  );

  dispatchIfDifferentRead(
    dispatch,
    "summary-read",
    {
      data: summaryQuery.data?.resourceDependentsSummary,
      isLoading: summaryQuery.isLoading,
      error: summaryQuery.error,
    },
    state.summary,
  );

  // Used to load the root page.
  const loadRootPage = useCallback(
    async (cursor: string) => {
      if (!snapshotToken) {
        return;
      }

      dispatch({ type: "root-page-requested", cursor });

      try {
        const result = await loadResourceDependentsPage({
          queryClient,
          resourceType,
          resourceId,
          cursor,
          snapshotToken,
        });
        dispatch({
          type: "root-page-loaded",
          snapshotToken,
          cursor,
          page: result.resourceDependentsPage,
        });
      } catch (error) {
        dispatch(
          isExpiredCursor(error)
            ? { type: "snapshot-expired", snapshotToken }
            : { type: "root-page-failed", snapshotToken, cursor },
        );
      }
    },
    [queryClient, resourceId, resourceType, snapshotToken],
  );

  // Used to load the children of a row.
  const loadChildren = useCallback(
    async (row: UsageRow) => {
      if (!snapshotToken) {
        return;
      }

      const cursor = row.childrenLoaded ? row.childCursor : null;
      dispatch({ type: "children-requested", key: row.key, cursor });

      try {
        const result = await loadResourceDependentsPage({
          queryClient,
          resourceType: row.node.resourceType,
          resourceId: row.node.resourceId,
          cursor,
          snapshotToken,
        });
        dispatch({
          type: "children-loaded",
          snapshotToken,
          key: row.key,
          cursor,
          page: result.resourceDependentsPage,
        });
      } catch (error) {
        dispatch(
          isExpiredCursor(error)
            ? { type: "snapshot-expired", snapshotToken }
            : { type: "children-failed", snapshotToken, key: row.key, cursor },
        );
      }
    },
    [queryClient, snapshotToken],
  );

  // Used to toggle the expansion of a row.
  const onToggle = useCallback(
    (row: UsageRow) => {
      dispatch({ type: "row-toggled", key: row.key });
      if (
        !row.childrenLoaded &&
        !row.isLoadingChildren &&
        row.directDependentCount > 0
      ) {
        void loadChildren(row);
      }
    },
    [loadChildren],
  );

  // In the case of an error, reload the page to try again.
  const reload = async () => {
    if (isReloading) {
      return;
    }
    setIsReloading(true);
    dispatch({ type: "reload-requested" });
    rebuildPoll.reset();
    try {
      await firstPageQuery.refetch();
    } finally {
      setIsReloading(false);
    }
  };

  // A reader who asks to keep waiting gets the full count of attempts again.
  const checkAgain = () => {
    rebuildPoll.reset();
    void firstPageQuery.refetch();
  };

  const page = state.firstPage.data;

  if (state.firstPage.isLoading) {
    return (
      <Column gap={3} p={4}>
        <SkeletonItem borderRadius="md" height={20} />
        <SkeletonItem borderRadius="md" height={10} />
        <UsageRowsSkeleton />
      </Column>
    );
  }

  // If the tree is not loaded, show an error message.
  if (!state.hasTree) {
    if (state.firstPage.error) {
      return (
        <EmptyState
          m={4}
          title="Couldn’t load usage"
          message={PAGE_LOAD_ERROR}
        />
      );
    }

    if (page?.status === LineageStatus.Rebuilding) {
      if (rebuildPoll.hasGivenUp) {
        return (
          <EmptyState
            m={4}
            title="Still calculating usage"
            message="This is taking longer than usual. Check again in a few minutes, or contact support if it keeps happening."
            actions={<Button onClick={checkAgain}>Check again</Button>}
          />
        );
      }

      return (
        <Column gap={3} p={4} role="status" aria-live="polite">
          <Row align="center" gap={2}>
            <Spinner size="sm" />
            <Text size="sm" color="text.secondary">
              Calculating usage
            </Text>
          </Row>
          <UsageRowsSkeleton />
        </Column>
      );
    }

    if (!page?.rows) {
      return (
        <EmptyState
          m={4}
          title="Couldn’t calculate usage"
          message={PAGE_LOAD_ERROR}
        />
      );
    }

    if (page.rows.length === 0) {
      return (
        <EmptyState
          variant="minimal"
          mx={4}
          my={10}
          title="Nothing uses this resource"
          message="No other resource in your workspace references this one."
        />
      );
    }
  }

  return (
    <Column gap={3} p={4} flex={fillHeight ? 1 : undefined} minHeight={0}>
      {!isTreeOnly &&
        (state.summaryCounts ? (
          <UsageSummary
            directDependents={state.summaryCounts.directDependents}
            indirectDependents={state.summaryCounts.indirectDependents}
            countsByType={state.countsByType}
            lastCalculatedAt={state.lastCalculatedAt}
          />
        ) : state.hasSummaryError ? (
          <Alert
            variant="inline"
            type="warning"
            title="Couldn’t load the usage summary"
            message={PAGE_LOAD_ERROR}
            actions={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void summaryQuery.refetch()}
              >
                Retry
              </Button>
            }
          />
        ) : (
          <SkeletonItem borderRadius="md" height={20} />
        ))}

      {!isTreeOnly && (
        <Row align="center" gap={2}>
          <Box flex={2}>
            <HiddenFormLabel label="Search loaded resources">
              <SearchInput
                width="100%"
                placeholder="Search loaded resources"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </HiddenFormLabel>
          </Box>
          <ResourceTypeFilter
            countsByType={state.countsByType}
            selectedTypes={state.typeFilter}
            onChange={(selectedTypes) =>
              dispatch({ type: "types-selected", selectedTypes })
            }
          />
        </Row>
      )}

      {page?.status === LineageStatus.Rebuilding &&
        (rebuildPoll.hasGivenUp ? (
          <Alert
            variant="inline"
            type="warning"
            title="Still calculating usage"
            message="This is taking longer than usual, so we stopped checking. These results are from the last completed run."
            actions={
              <Button size="sm" variant="secondary" onClick={checkAgain}>
                Check again
              </Button>
            }
          />
        ) : (
          <Row align="center" gap={2} role="status" aria-live="polite">
            <Spinner size="sm" />
            <Text size="sm" color="text.secondary">
              Recalculating usage. These results are from the last completed
              run.
            </Text>
          </Row>
        ))}

      {state.firstPage.error && (
        <Alert
          variant="inline"
          type="warning"
          title="Couldn’t check for newer results"
          message={PAGE_LOAD_ERROR}
        />
      )}

      {hasExpired && (
        <Alert
          variant="inline"
          type="warning"
          title="Usage changed while this page was open"
          message="These results are from an earlier calculation, so you can’t load more rows. Reload to see the latest usage."
          actions={
            <Button
              size="sm"
              isLoading={isReloading}
              onClick={() => void reload()}
            >
              Reload
            </Button>
          }
        />
      )}

      <UsageTree
        fillHeight={fillHeight}
        state={state}
        filter={{ types: state.typeFilter, search }}
        onToggle={onToggle}
        onCollapseAll={() => dispatch({ type: "collapsed-all" })}
        onLoadMoreChildren={loadChildren}
        onLoadMorePage={loadRootPage}
        isLoadingDisabled={hasExpired}
        headerActions={
          canExportCsv && (
            <Button
              icon={DownloadIcon}
              size="sm"
              variant="secondary"
              isDisabled={!snapshotToken || hasExpired}
              isLoading={isExporting}
              onClick={() => {
                // The tree only holds what has been expanded, so the export
                // reads the whole subtree, pinned to the snapshot shown here.
                if (snapshotToken) {
                  void exportLineage({
                    kind: "resource",
                    resourceType,
                    resourceId,
                    snapshotToken,
                    onSnapshotExpired: () =>
                      dispatch({ type: "snapshot-expired", snapshotToken }),
                  });
                }
              }}
            >
              Download CSV
            </Button>
          )
        }
        emptyState={
          state.typeFilter !== "all" && state.typeFilter.size === 0 ? (
            <EmptyState
              m={4}
              title="No resource types selected"
              message={`Select at least one resource type to see what uses this ${getDisplayNameForResource(resourceType).lowercaseLabel}.`}
              // The resource type filter unmounts while the summary is missing, so this keeps a way out.
              actions={
                <Button
                  onClick={() =>
                    dispatch({ type: "types-selected", selectedTypes: "all" })
                  }
                >
                  Select all types
                </Button>
              }
            />
          ) : (
            <EmptyState
              m={4}
              title="No matching resources"
              message="No loaded resource matches these filters. Only loaded resources are searched, so try clearing a filter or loading more."
            />
          )
        }
      />
    </Column>
  );
};

const UsagePanelHeader: FC<UsagePanelHeaderProps> = ({
  resourceType,
  name,
  lastUpdatedAt,
}) => {
  const display = getDisplayNameForResource(resourceType);
  const Icon = display.icon;
  const relativeUpdatedAt = lastUpdatedAt
    ? formatRelativeShort(lastUpdatedAt)
    : "";
  const absoluteUpdatedAt = lastUpdatedAt
    ? formatDatetimeWithTimezone(lastUpdatedAt)
    : null;

  return (
    <Column
      gap={3}
      p={4}
      borderBottomWidth="hairline"
      borderBottomColor="base.divider"
      flexShrink={0}
    >
      <Row align="center" gap={2} minWidth={0}>
        <IconBox
          size="md"
          color={display.color}
          icon={<Icon />}
          aria-hidden
          display="flex"
        />
        <Tooltip message={name}>
          <Heading size="md" isTruncated>
            {name}
          </Heading>
        </Tooltip>
      </Row>

      <Row align="center" gap={2}>
        <Text color="text.secondary">{display.label}</Text>
        {relativeUpdatedAt && (
          <>
            <Box
              height={5}
              borderLeftWidth="hairline"
              borderLeftColor="base.divider"
              aria-hidden
            />
            <Tooltip message={absoluteUpdatedAt ?? lastUpdatedAt}>
              <Text color="text.secondary">Updated {relativeUpdatedAt}</Text>
            </Tooltip>
          </>
        )}
      </Row>
    </Column>
  );
};
